import { useMemo } from 'react';
import { format } from 'date-fns';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { getDeadlineForWeek } from '@/services/timesheetSettingsService';

export type DeadlineWarning =
  | { type: 'overdue'; daysOverdue: number; deadline: Date; message: string }
  | { type: 'approaching'; daysUntilDeadline: number; deadline: Date; message: string };

/**
 * Deadline warning for the current week's timesheet, derived from the
 * organisation deadline settings. Only draft timesheets are warned about.
 */
export function useDeadlineWarning(
  organizationSettings: OrganisationTimesheetSettings | undefined,
  currentWeekStart: Date,
  status: string
): DeadlineWarning | null {
  return useMemo(() => {
    const deadlineSettings = organizationSettings?.deadline;
    if (!organizationSettings || !deadlineSettings) return null;

    // Only show warnings for draft timesheets
    if (status !== 'draft') return null;

    const now = new Date();
    const deadline = getDeadlineForWeek(currentWeekStart, {
      ...organizationSettings,
      deadline: { ...deadlineSettings, deadlineTime: deadlineSettings.deadlineTime || '17:00' },
    });

    const diffMs = deadline.getTime() - now.getTime();
    const daysUntilDeadline = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const reminderDays = deadlineSettings.reminderDaysBefore ?? 2;

    if (daysUntilDeadline < 0) {
      const daysOverdue = Math.abs(daysUntilDeadline);
      return {
        type: 'overdue' as const,
        daysOverdue,
        deadline,
        message: `Your timesheet for the week of ${format(currentWeekStart, 'dd MMM yyyy')} is ${daysOverdue} day${daysOverdue > 1 ? 's' : ''} overdue.`,
      };
    }

    if (daysUntilDeadline <= reminderDays) {
      return {
        type: 'approaching' as const,
        daysUntilDeadline,
        deadline,
        message: daysUntilDeadline === 0
          ? `Your timesheet is due today by ${format(deadline, 'h:mm a')}.`
          : `Your timesheet is due in ${daysUntilDeadline} day${daysUntilDeadline > 1 ? 's' : ''} (${format(deadline, 'EEEE, dd MMM')} at ${format(deadline, 'h:mm a')}).`,
      };
    }

    return null;
  }, [organizationSettings, status, currentWeekStart]);
}
